import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useStore } from "@/store/useStore";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Shield, Clock, Loader2, AlertCircle, CheckCircle2, XCircle, Camera, Smartphone, Info } from "lucide-react";
import { API_URL } from "@/config/api";

const PROVIDER_LABEL = { didit: "Didit", mts: "МТС ID", mock: "Test", "didit-mock": "Didit (test)", "mts-mock": "МТС ID (test)" };

export default function KYCVerification({ onComplete }) {
  const { t } = useTranslation();
  const { token, user, fetchUser } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(null);
  const [kyc, setKyc] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const handledRef = useRef(false);

  const authed = { headers: { Authorization: `Bearer ${token}` } };

  const fetchKycStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/kyc/status`, authed);
      if (res.ok) setKyc(await res.json());
    } catch (err) {
      console.error("Failed to fetch KYC status:", err);
    }
  };

  useEffect(() => { fetchKycStatus(); }, []);

  // Return from a provider: ?kyc=didit[&verificationSessionId=..] or ?kyc=mts&result=approved|cancelled|incomplete|error
  useEffect(() => {
    const provider = searchParams.get("kyc");
    if (!provider || handledRef.current) return;
    handledRef.current = true;
    const result = searchParams.get("result");
    const finish = async (status) => {
      await Promise.all([fetchKycStatus(), fetchUser()]);
      await new Promise((r) => setTimeout(r, 300));
      if (status === "approved") { toast.success(t("kyc.approved")); onComplete?.(); }
      else if (status === "rejected") toast.error(t("kyc.rejected"));
      else if (status === "pending") toast.info(t("kyc.submitted"));
      setSearchParams({}, { replace: true });
    };
    if (provider === "didit") {
      setSyncing(true);
      fetch(`${API_URL}/api/kyc/didit/sync`, { method: "POST", ...authed })
        .then((r) => r.json()).then((d) => finish(d.status)).catch(() => finish(null)).finally(() => setSyncing(false));
    } else if (provider === "mts") {
      // Defer: on a full page load this effect runs before <Toaster/> subscribes
      setTimeout(() => {
        if (result === "cancelled") toast.info(t("kyc.cancelled"));
        else if (result === "incomplete") toast.error(t("kyc.incomplete"));
        else if (result === "error") toast.error(t("kyc.error"));
        finish(result === "approved" ? "approved" : null);
      }, 300);
    }
  }, [searchParams]);

  const start = async (provider) => {
    setLoading(provider);
    try {
      const res = await fetch(`${API_URL}/api/kyc/${provider}/start`, { method: "POST", ...authed });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || t("common.error"));
      if (data.mock) { toast.info(t("kyc.testMode")); await fetchKycStatus(); return; }
      window.location.assign(data.url);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(null);
    }
  };

  const approveMock = async () => {
    setLoading("mock");
    try {
      const res = await fetch(`${API_URL}/api/kyc/approve-mock`, { method: "POST", ...authed });
      if (!res.ok) throw new Error((await res.json()).detail || t("common.error"));
      toast.success(t("kyc.approved"));
      await Promise.all([fetchKycStatus(), fetchUser()]);
      onComplete?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(null);
    }
  };

  const status = kyc?.status || user?.kyc_status || "not_started";
  const isVerified = status === "approved";
  const canStart = !isVerified;

  const badge = {
    approved: ["bg-green-500/20 text-green-500 border-green-500/30", CheckCircle2, t("profile.kyc.approved")],
    pending: ["bg-yellow-500/20 text-yellow-500 border-yellow-500/30", Clock, t("profile.kyc.pending")],
    rejected: ["bg-red-500/20 text-red-500 border-red-500/30", XCircle, t("kyc.rejected")],
    retry: ["bg-orange-500/20 text-orange-500 border-orange-500/30", AlertCircle, t("kyc.retry")],
  }[status] || ["", Shield, t("profile.kyc.notStarted")];
  const BadgeIcon = badge[1];

  const providers = [
    { id: "didit", icon: Camera, title: t("kyc.diditTitle"), desc: t("kyc.diditDesc"), configured: kyc?.providers?.didit?.configured },
    { id: "mts", icon: Smartphone, title: t("kyc.mtsTitle"), desc: t("kyc.mtsDesc"), configured: kyc?.providers?.mts?.configured },
  ];

  return (
    <Card className="glass border-border/50" data-testid="kyc-verification">
      <CardHeader>
        <CardTitle className="text-lg flex items-center justify-between">
          <span className="flex items-center gap-2">
            {isVerified ? <ShieldCheck className="h-5 w-5 text-green-500" /> : <ShieldAlert className="h-5 w-5 text-yellow-500" />}
            {t("profile.kyc.title")}
          </span>
          <Badge className={badge[0]} variant={status === "not_started" ? "outline" : undefined} data-testid="kyc-status-badge">
            <BadgeIcon className="h-3 w-3 mr-1" />{badge[2]}
          </Badge>
        </CardTitle>
        <CardDescription>{t("profile.kyc.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {syncing && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />{t("kyc.returned")}</div>
        )}

        {isVerified ? (
          <div className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10" data-testid="kyc-verified-box">
            <CheckCircle2 className="h-8 w-8 text-green-500 shrink-0" />
            <div>
              <p className="font-semibold text-green-500">{t("kyc.verifiedTitle")}</p>
              <p className="text-sm text-muted-foreground">
                {kyc?.full_name ? `${kyc.full_name} · ` : ""}{t("kyc.verifiedVia")} {PROVIDER_LABEL[kyc?.provider] || kyc?.provider}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex gap-3 p-3 rounded-lg bg-muted/30 text-sm" data-testid="kyc-why">
              <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">{t("kyc.whyTitle")}</p>
                <p className="text-muted-foreground">{t("kyc.whyText")}</p>
              </div>
            </div>

            {status === "pending" && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-yellow-500/10">
                <Clock className="h-6 w-6 text-yellow-500 shrink-0" />
                <div>
                  <p className="font-semibold text-yellow-500 text-sm">{t("kyc.pendingTitle")}</p>
                  <p className="text-xs text-muted-foreground">{t("kyc.pendingDescription")}</p>
                </div>
              </div>
            )}
            {(status === "rejected" || status === "retry") && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-red-500/10" data-testid="kyc-rejected-box">
                <XCircle className="h-6 w-6 text-red-500 shrink-0" />
                <div>
                  <p className="font-semibold text-red-500 text-sm">{status === "rejected" ? t("kyc.rejectedTitle") : t("kyc.retryTitle")}</p>
                  <p className="text-xs text-muted-foreground">{kyc?.rejection_reason || t("kyc.rejectedDescription")}</p>
                </div>
              </div>
            )}

            {canStart && (
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">{t("kyc.chooseMethod")}</p>
                {providers.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 p-3 rounded-lg border border-border/60 bg-background/40" data-testid={`kyc-provider-${p.id}`}>
                    <div className="h-10 w-10 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0"><p.icon className="h-5 w-5" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm">{p.title}</p>
                      <p className="text-xs text-muted-foreground">{p.desc}</p>
                      {kyc && !p.configured && <p className="text-[11px] text-yellow-500 mt-0.5">{t("kyc.testMode")}</p>}
                    </div>
                    <Button size="sm" onClick={() => start(p.id)} disabled={!!loading} data-testid={`kyc-start-${p.id}`}>
                      {loading === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : (status === "retry" || status === "rejected" ? t("kyc.tryAgain") : t("kyc.start"))}
                    </Button>
                  </div>
                ))}
                {kyc?.mock && status === "pending" && (
                  <Button onClick={approveMock} variant="outline" className="w-full" disabled={!!loading} data-testid="kyc-approve-mock">
                    {loading === "mock" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                    {t("kyc.approveMock")}
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
